const SuperPlummaContext = React.createContext(null);

function SuperPlummaChat({ session, state, onMinimize, onReset, allowExpand = true, accessory, intro, disabled = false }) {
  const [input, setInput] = useState('');
  const [listening, setListening] = useState(false);
  const [voiceError, setVoiceError] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [pageWidth, setPageWidth] = useState(1400);
  const expandButton = useRef(null);
  const wasExpanded = useRef(false);
  const speech = useRef(null);
  const scroll = useRef(null);
  // Home and floating views never mount together, so one stable label target is enough.
  const inputId = 'superplumma-chat-input';
  const stopSpeech = () => { speech.current?.(); speech.current = null; setListening(false); };
  useEffect(() => () => speech.current?.(), []);
  useEffect(() => { if (state.approval || state.form) setExpanded(false); }, [state.approval, state.form]);
  useEffect(() => {
    if (scroll.current) scroll.current.scrollTop = scroll.current.scrollHeight;
  }, [state.messages, state.busy, expanded]);
  useEffect(() => {
    if (!expanded) {
      if (wasExpanded.current) expandButton.current?.focus();
      wasExpanded.current = false;
      return;
    }
    wasExpanded.current = true;
    const modal = document.querySelector('.superplumma-chat-modal');
    modal?.querySelector('textarea')?.focus();
    const page = document.querySelector('.main-inner');
    const update = () => setPageWidth(page?.getBoundingClientRect().width || 1400);
    update();
    const observer = new ResizeObserver(update);
    if (page) observer.observe(page);
    const trapFocus = event => {
      if (event.key !== 'Tab') return;
      const controls = Array.from(modal?.querySelectorAll('button:not(:disabled), textarea:not(:disabled), input:not(:disabled), [href], [tabindex="0"]') || []);
      const first = controls[0], last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    };
    modal?.addEventListener('keydown', trapFocus);
    return () => { observer.disconnect(); modal?.removeEventListener('keydown', trapFocus); };
  }, [expanded]);
  const toggleSpeech = () => {
    if (listening) { stopSpeech(); return; }
    setVoiceError('');
    try {
      speech.current = window.PlummeInvoiceControlSpeech.start({
        lang: PlummeI18n.intlLocale(),
        onText: (text) => setInput(current => [current, text].filter(Boolean).join(' ').slice(0, 2000)),
        onEnd: () => { speech.current = null; setListening(false); },
        onError: (key) => { setVoiceError(key); stopSpeech(); },
      });
      setListening(true);
    } catch (error) { setVoiceError(error.message); }
  };
  const send = (question = input) => {
    if (!question.trim() || state.busy || listening || disabled) return;
    setInput(''); setVoiceError(''); void session.ask(question.trim());
  };
  const chat = <Card className="col dash-assistant superplumma-chat">
    {!expanded && <div className="superplumma-head">
      <div className="grow"><div className="t-h3">{t('aii.superPlumma')}</div><div className="t-sm muted">{t('home.assistant.sub')}</div></div>
      <img src={window.__resources?.superPlummaHero || 'src/img/SuperPlumma.png'} alt="" />
      {(allowExpand || onMinimize) && <div className="superplumma-window-actions">
        {allowExpand && <button ref={expandButton} type="button" className="superplumma-expand" onClick={() => setExpanded(true)} aria-label={t('control.expand')} title={t('control.expand')} aria-haspopup="dialog"><Icon name="expand" size={18}/></button>}
        {onMinimize && <button type="button" className="superplumma-minimize" onClick={() => { stopSpeech(); onMinimize(); }} aria-label={t('control.minimize')}>−</button>}
      </div>}
    </div>}
    <div className="panel superplumma-log" ref={scroll} role="log" aria-live="polite">
      {!state.messages.length && <div className="t-sm muted">{intro || t('home.assistant.intro')}</div>}
      {state.messages.map((message, index) => <div key={index} className={`superplumma-message ${message.role}`} aria-label={message.role === 'user' ? t('control.you') : t('aii.superPlumma')}>{message.content}</div>)}
      {state.busy && <div className="t-sm muted" role="status">{t(state.progress || 'control.working')}</div>}
      {state.saved && <div className="t-sm" role="status">{t('control.saved')}</div>}
      {state.review && <div className="t-sm" role="status">{t('control.reviewHelp')}</div>}
      {(state.error || voiceError) && <div className="invoice-control-error" role="alert">{t(state.error || voiceError, state.error || voiceError)}</div>}
    </div>
    {!state.messages.length && <div className="row wrap gap-6">{[1, 2, 3].map(n => <button key={n} type="button" className="badge badge-muted" disabled={state.busy || listening} onClick={() => send(t(`home.assistant.sugg${n}`))}>{t(`home.assistant.sugg${n}`)}</button>)}</div>}
    <form onSubmit={event => { event.preventDefault(); send(); }}>
      <label className="superplumma-sr-only" htmlFor={inputId}>{t('control.input')}</label>
      <div className="superplumma-composer">
        <textarea id={inputId} rows={1} maxLength={2000} disabled={disabled} value={input} onChange={event => setInput(event.target.value)} placeholder={t('home.assistant.placeholder')} onKeyDown={event => {
          if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) { event.preventDefault(); send(); }
        }} />
        <button type="button" disabled={state.busy || disabled} aria-pressed={listening} aria-label={t(listening ? 'control.stopMic' : 'control.speak')} title={t(listening ? 'control.stopMic' : 'control.speak')} onClick={toggleSpeech}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true"><rect x="9" y="2" width="6" height="12" rx="3"/><path d="M5 10v2a7 7 0 0 0 14 0v-2M12 19v3M8 22h8"/></svg>
        </button>
        {state.busy ? <button key="stop" type="button" className="superplumma-send" aria-label={t('control.stop')} title={t('control.stop')} onClick={() => { stopSpeech(); session.stop(); }} data-help={t('helpTopics.assistantRun.body')} data-help-title={t('helpTopics.assistantRun.title')}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><rect x="5" y="5" width="14" height="14" rx="2"/></svg>
        </button> : <button key="send" type="submit" className="superplumma-send" disabled={!input.trim() || listening} aria-label={t('control.run')} title={t('control.run')} data-help={t('helpTopics.assistantRun.body')} data-help-title={t('helpTopics.assistantRun.title')}><Icon name="send" size={18}/></button>}
      </div>
      {listening && <div className="t-sm muted" role="status">{t('control.listening')}</div>}
    </form>
    {accessory}
    <button type="button" className="superplumma-reset" disabled={state.busy || disabled} onClick={() => {
      if (state.busy || disabled || !session.reset()) return;
      stopSpeech(); setInput(''); setVoiceError('');
      onReset?.();
    }}>{t('control.newChat')}</button>
  </Card>;
  return expanded ? ReactDOM.createPortal(<div data-assistant-exclude role="dialog" aria-modal="true" aria-label={t('aii.superPlumma')}>
    <Modal open center max={pageWidth} className="superplumma-chat-modal" onClose={() => setExpanded(false)}
      title={t('aii.superPlumma')} titleRight={<button type="button" className="superplumma-expand" onClick={() => setExpanded(false)} aria-label={t('control.collapse')} title={t('control.collapse')}><Icon name="shrink" size={18}/></button>}>
      {chat}
    </Modal>
  </div>, document.body) : chat;
}
Object.assign(window, { SuperPlummaChat, SuperPlummaContext });
